import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../components/ui/dialog";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { ArrowLeft, ArrowRight, CalendarPlus, CaretLeft, CaretRight, CheckCircle, PencilSimple, XCircle, Trash, Printer, ArrowUUpLeft, Receipt, CopySimple } from "@phosphor-icons/react";
import { toast } from "sonner";
import { BookingForm } from "../components/BookingForm";
import { DateTimeField, MoneyInput } from "../components/booking/pickers";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { QuoteDialog } from "../components/QuoteDialog";
import { InvoiceDialog } from "../components/InvoiceDialog";
import { permVisible } from "../lib/permissions";
import { useAuth } from "../contexts/AuthContext";
import { validateBooking, TAX_RATE, bookingTotal, extensionDays } from "./bookingFields";
import ChauffeurSplitSection from "./ChauffeurSplitSection";
import { driverPayFor } from "../lib/chauffeurSplit";

const STATUS_LABEL = {
  submitted: "Submitted", rejected: "Sent Back / Rejected", pre_booking: "Pre-booking", current: "Current / Out",
  completed: "Completed", cancelled: "Cancelled", confirmed: "Confirmed",
};

// SALES-014: the car is out, or committed and about to go out.
const EXTENDABLE = ["pre_booking", "confirmed", "current"];
// SALES-027: extension DAYS are inclusive CALENDAR days (see `extensionDays` in
// bookingFields) — the same rule rentals bill by since SALES-023, so the dialog,
// the history and a later points formula agree. `naiveMs` reads a naive
// wall-clock string as if UTC (matching the server's naive arithmetic) and is
// used only to tell whether the new end is LATER than the current end: a
// same-day-but-later return is a valid 0-day extension, so the gate is "later",
// not "≥ 1 day".
const naiveMs = (s) => (s ? Date.parse(`${String(s).replace("Z", "")}Z`) : NaN);
const isLater = (from, to) => {
  const a = naiveMs(from), b = naiveMs(to);
  return Number.isFinite(a) && Number.isFinite(b) && b > a;
};
const dayLabel = (n) => `${n} day${n === 1 ? "" : "s"}`;

// SALES-028: the rep a booking's points go to by default — the first name
// listed, mirroring the server's `credited_rep` (a shared "Leon/Emal" credits
// Leon). Kept in step with the backend definition; the override still writes
// through the points-rep route, which validates the chosen name.
const creditedRep = (raw) => {
  const s = (raw || "").trim();
  const low = s.toLowerCase();
  let cut = s.length;
  for (const sep of ["/", "+", "&", " and "]) {
    const i = low.indexOf(sep);
    if (i !== -1) cut = Math.min(cut, i);
  }
  return s.slice(0, cut).trim();
};

function Field({ label, value }) {
  if (value === undefined || value === null || value === "") return null;
  return (
    <div>
      <div className="label-mono mb-1">{label}</div>
      <div className="text-sm" data-testid={`detail-${label.toLowerCase().replace(/\s+/g, "-")}`}>{value}</div>
    </div>
  );
}

function Section({ title, children }) {
  const has = Array.isArray(children) ? children.some(Boolean) : Boolean(children);
  if (!has) return null;
  return (
    <div className="border border-border bg-card p-6">
      <div className="label-mono text-primary mb-4">{title}</div>
      {/* The single place the detail cards' column count is decided. Measured
          on a real booking: the widest VALUE is a formatted datetime at 185px
          ("Mon, Sep 21, 2026, 12:00 AM") and the widest LABEL is ~210px
          ("Rental coverage on policy" on a website booking), so a column needs
          ~215px to never wrap. 4 columns hold 277px at 1536 and 373px at 1920;
          a 5th would be 218px at 1536, which wraps. Extra width therefore buys
          more fields per row, not wider fields. `col-span-full` children (Tax,
          Notes) span whatever the count is and are unaffected. */}
      <div className="grid grid-cols-2 md:grid-cols-3 2xl:grid-cols-4 gap-5">{children}</div>
    </div>
  );
}

export default function BookingDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  // Return-to context passed from the list page (Bookings.jsx / MyBookings.jsx).
  // Falls back to the browser history when the user landed here from an
  // external link (e.g. a notification email).
  const returnTo = location.state?.returnTo;
  const listIds = Array.isArray(location.state?.listIds) ? location.state.listIds : [];
  const idx = listIds.indexOf(id);
  const prevId = idx > 0 ? listIds[idx - 1] : null;
  const nextId = idx >= 0 && idx < listIds.length - 1 ? listIds[idx + 1] : null;
  // Flush any unsaved split-section edits before leaving this booking.
  const splitRef = useRef(null);
  const goBack = async () => {
    await splitRef.current?.saveIfDirty?.();
    if (returnTo) navigate(returnTo); else navigate(-1);
  };
  const goSibling = async (sibId) => {
    if (!sibId) return;
    await splitRef.current?.saveIfDirty?.();
    navigate(`/bookings/view/${sibId}`, { state: { returnTo, listIds }, replace: true });
  };
  // Admin/staff, or any login granted one of the booking modules.
  const canManage = user?.role === "admin" || user?.role === "staff"
    || ["rental", "chauffeur", "tours", "submitted"].some((p) => permVisible(user, p));
  // SALES-028: only admin and office staff can change who a booking's points go
  // to (a rep must not be able to award themselves points). Everyone else sees
  // the current recipient as a read-only line.
  const canEditPoints = user?.role === "admin" || user?.role === "staff";
  const [b, setB] = useState(null);
  const [loading, setLoading] = useState(true);
  const [vehicles, setVehicles] = useState([]);
  const [chauffeurs, setChauffeurs] = useState([]);
  const [salespeople, setSalespeople] = useState([]);
  const [editOpen, setEditOpen] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [quoteBooking, setQuoteBooking] = useState(null);
  const [invoiceBooking, setInvoiceBooking] = useState(null);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [extendOpen, setExtendOpen] = useState(false);
  const [extendEnd, setExtendEnd] = useState("");
  const [extendRate, setExtendRate] = useState("");
  const [extendAmount, setExtendAmount] = useState("");
  const [amountTouched, setAmountTouched] = useState(false);
  const [extendNotes, setExtendNotes] = useState("");
  const [extending, setExtending] = useState(false);

  const formType = b?.booking_type === "rental" ? "rental" : b?.booking_type === "internal" ? "internal" : b?.booking_type === "tour" ? "tour" : "chauffeur";

  const loadBooking = async () => {
    try {
      const { data } = await api.get(`/bookings/${id}`);
      setB(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  useEffect(() => { loadBooking(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    // Handle return from Stripe Checkout (?payment=success&session_id=... or ?payment=cancelled)
    const params = new URLSearchParams(window.location.search);
    const payment = params.get("payment");
    const sessionId = params.get("session_id");
    if (payment === "cancelled") {
      toast.info("Payment was cancelled.");
      window.history.replaceState({}, "", window.location.pathname);
    } else if (payment === "success" && sessionId) {
      let attempts = 0;
      const poll = async () => {
        try {
          const { data } = await api.get(`/payments/checkout/status/${sessionId}`);
          if (data.payment_status === "paid") {
            toast.success("Payment received — booking marked as paid.");
            window.history.replaceState({}, "", window.location.pathname);
            loadBooking();
            return;
          }
          if (data.status === "expired") {
            toast.error("Payment session expired.");
            window.history.replaceState({}, "", window.location.pathname);
            return;
          }
        } catch (e) { console.error("status poll failed", e); }
        if (++attempts < 5) setTimeout(poll, 2000);
        else { toast.info("Still processing payment — refresh shortly to confirm."); window.history.replaceState({}, "", window.location.pathname); }
      };
      poll();
    }
    /* eslint-disable-next-line */
  }, []);
  useEffect(() => {
    (async () => {
      try {
        const [v, c] = await Promise.all([api.get("/vehicles"), api.get("/chauffeurs")]);
        setVehicles(v.data); setChauffeurs(c.data);
      } catch (e) { console.error("Failed to load vehicles/chauffeurs for edit form", e); }
    })();
  }, []);
  // SALES-028: the salespeople list backs the "Points go to" dropdown. Only
  // admin/staff can change it, so only they need the list.
  useEffect(() => {
    if (!canEditPoints) return;
    (async () => {
      try { const { data } = await api.get("/salespeople"); setSalespeople(data); }
      catch (e) { console.error("Failed to load salespeople for points override", e); }
    })();
  }, [canEditPoints]);
  // SALES-014: rate x days pre-fills the amount until the rep types their own
  // figure. An extension is often re-rated cheaper than the original booking,
  // so the amount stays editable — and the arithmetic lives here because
  // nothing in the backend prices from a rate.
  useEffect(() => {
    if (amountTouched) return;
    const days = extensionDays(b?.end_datetime, extendEnd);
    const rate = Number(extendRate);
    setExtendAmount(extendRate !== "" && days > 0 && rate >= 0
      ? String(Math.round(rate * days * 100) / 100)
      : "");
  }, [extendRate, extendEnd, amountTouched, b?.end_datetime]);

  const doTransition = async (status, label) => {
    try {
      await api.post(`/bookings/${id}/transition?new_status=${status}`);
      toast.success(`Moved to ${label}`);
      loadBooking();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const doDelete = async () => {
    const reason = window.prompt("Delete this booking? It moves to the recycle bin (Settings → Deleted bookings), where an admin can restore it.\n\nOptional reason:");
    if (reason === null) return;
    try {
      await api.delete(`/bookings/${id}`, { params: { reason } });
      toast.success("Booking moved to recycle bin");
      navigate(-1);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  // SALES-024: reps cancel through the same transition the admin Cancel button
  // uses, with a confirm step first (cancel frees the car for its dates). Admin
  // and staff keep their existing one-click behaviour, unchanged.
  const cancelBooking = () => {
    if (!canManage && !window.confirm("Cancel this booking? This frees the car for these dates. It keeps the record — it does not delete it.")) return;
    doTransition("cancelled", "Cancelled");
  };

  const toggleTax = async () => {
    try {
      await api.put(`/bookings/${id}`, { ...b, tax_applied: !b.tax_applied });
      toast.success(!b.tax_applied ? "Tax added to total" : "Tax removed");
      loadBooking();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  // SALES-028: change who this booking's points go to. Points only — the
  // salesperson and every revenue figure stay as they are.
  const setPointsRep = async (val) => {
    try {
      await api.patch(`/bookings/${id}/points-rep`, { points_rep: val === "__default__" ? "" : val });
      toast.success(val === "__default__" ? "Points set back to the salesperson" : "Points recipient updated");
      loadBooking();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const sendBack = async () => {
    try {
      await api.post(`/bookings/${id}/transition`, null, { params: { new_status: "rejected", reason: rejectReason } });
      toast.success("Sent back to agent");
      setRejectOpen(false); setRejectReason("");
      loadBooking();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const openEdit = () => { setForm({ ...b }); setEditOpen(true); };

  const doExtend = async () => {
    setExtending(true);
    try {
      await api.post(`/bookings/${id}/extend`, {
        new_end_datetime: extendEnd,
        notes: extendNotes,
        // Flat amount typed with no rate -> null, which is what the entry stores.
        rate: extendRate === "" ? null : Number(extendRate),
        // Always sent, never omitted: a blank field is refused by the route so
        // a free extension has to be a deliberate 0.
        amount_added: extendAmount === "" ? 0 : Number(extendAmount),
      });
      toast.success("Extension recorded");
      setExtendOpen(false);
      loadBooking();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setExtending(false); }
  };

  const handleVehicleChange = (vid) => {
    const v = vehicles.find((x) => x.id === vid);
    setForm((f) => ({ ...f, vehicle_id: vid, vehicle_label: v ? `${v.make} ${v.model}${v.year ? " " + v.year : ""}` : "", vehicle_plate: v?.license_plate || "" }));
  };
  const handleDriverChange = (did) => {
    const d = chauffeurs.find((x) => x.id === did);
    setForm((f) => {
      const pay = driverPayFor({ ...f, booking_type: formType }, d);
      return {
        ...f,
        driver_id: did,
        driver_name: d ? `${d.first_name} ${d.last_name}` : "",
        driver_cost: Number(f.driver_cost) > 0 ? f.driver_cost : (pay || f.driver_cost),
      };
    });
  };

  const saveEdit = async () => {
    const err = validateBooking(form, formType);
    if (err) { toast.error(err); return; }
    setSaving(true);
    try {
      const payload = { ...form };
      await api.put(`/bookings/${id}`, payload);
      toast.success("Booking updated");
      setEditOpen(false);
      loadBooking();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="text-muted-foreground">Loading booking…</div>;
  if (!b) return <div className="text-destructive">Booking not found.</div>;

  const fmtDT = (s) => s ? new Date(s).toLocaleString(undefined, { weekday: "short", month: "short", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "";
  const money = (n) => (n || n === 0) && n !== "" ? `$${n}` : "";
  const yn = (v) => v === "yes" ? "Yes" : v === "no" ? "No" : v;
  const cap = (v) => v ? String(v).charAt(0).toUpperCase() + String(v).slice(1) : v;
  const policy = (v) => v === "opcf" ? "OPCF" : v === "liability" ? "Only Liability" : v;
  const active = b.status !== "cancelled" && b.status !== "completed";
  // SALES-022: an internal hold has no lifecycle and no money — only Edit and
  // Cancel apply (admin/staff), plus admin Delete. Every sale/pricing/invoice
  // action and the client/financial sections are hidden.
  const isInternal = b.booking_type === "internal";
  // Same gate as Edit — extending IS editing, so it follows the edit rule and
  // needs no permission key of its own. Rental only: chauffeur and tour jobs
  // are hourly, not multi-day rentals.
  const canExtend = canManage && formType === "rental" && EXTENDABLE.includes(b.status);
  const extensions = Array.isArray(b.extensions) ? b.extensions : [];
  const extendDays = extensionDays(b.end_datetime, extendEnd);
  // A same-day-but-later return adds 0 calendar days yet is still a valid
  // extension, so the gate is "later than the current end", not "≥ 1 day".
  const extendIsLater = isLater(b.end_datetime, extendEnd);
  // The same precedence the money code uses: `booking_revenue` is
  // `num(total) or num(amount)`, so an imported booking priced in `amount`
  // adds onto its real price and not onto a zero.
  const baseTotal = Number(b.total || b.amount) || 0;
  const addAmount = extendAmount === "" ? 0 : Number(extendAmount) || 0;
  const newTotal = Math.round((baseTotal + addAmount) * 100) / 100;
  const openExtend = () => {
    setExtendEnd(b.end_datetime || "");
    setExtendRate(""); setExtendAmount(""); setAmountTouched(false); setExtendNotes("");
    setExtendOpen(true);
  };

  return (
    <div className="space-y-6" data-testid="booking-detail-page">
      {/* Stacked, not `justify-between`: uncapped, a single row would have
          pushed the action buttons to the far right edge as the screen grew.
          Stacking is exactly where they already sit (they wrapped to their own
          line at 896px) and it cannot move with width. */}
      <div className="flex flex-col gap-3">
        <div className="flex items-center gap-2">
          <Button variant="ghost" onClick={goBack} data-testid="booking-detail-back">
            <ArrowLeft size={16} className="mr-1.5" /> Back
          </Button>
          {listIds.length > 1 && (
            <div className="flex items-center gap-1 border border-border rounded-md" data-testid="booking-detail-pager">
              <Button variant="ghost" size="sm" onClick={() => goSibling(prevId)} disabled={!prevId} data-testid="booking-detail-prev" title="Previous booking">
                <CaretLeft size={14} />
              </Button>
              <span className="text-xs text-muted-foreground px-1 select-none" data-testid="booking-detail-position">
                {idx + 1} / {listIds.length}
              </span>
              <Button variant="ghost" size="sm" onClick={() => goSibling(nextId)} disabled={!nextId} data-testid="booking-detail-next" title="Next booking">
                <CaretRight size={14} />
              </Button>
            </div>
          )}
        </div>
        <div className="flex items-center gap-2 flex-wrap" data-testid="booking-detail-actions">
          {!isInternal && canManage && b.status === "submitted" && (
            <Button size="sm" onClick={() => doTransition("pre_booking", "Pre Bookings")} className="bg-primary text-primary-foreground hover:bg-primary/90" data-testid="action-confirm"><CheckCircle size={14} className="mr-1.5" />Confirm</Button>
          )}
          {!isInternal && canManage && b.status === "submitted" && (
            <Button size="sm" variant="outline" onClick={() => { setRejectReason(""); setRejectOpen(true); }} data-testid="action-send-back" title="Send back to agent for edits"><ArrowUUpLeft size={14} className="mr-1.5 text-amber-500" />Send back</Button>
          )}
          {!isInternal && canManage && b.status === "pre_booking" && (
            <Button size="sm" onClick={() => doTransition("current", "Current")} className="bg-primary text-primary-foreground hover:bg-primary/90" data-testid="action-send-out"><ArrowRight size={14} className="mr-1.5" />Send out</Button>
          )}
          {!isInternal && canManage && b.status === "current" && (
            <Button size="sm" onClick={() => doTransition("completed", "Completed")} variant="outline" data-testid="action-complete"><CheckCircle size={14} className="mr-1.5" />Complete</Button>
          )}
          {!isInternal && (canExtend || b.can_extend) && (
            <Button size="sm" variant="outline" onClick={openExtend} data-testid="action-extend" title="Record a rental extension">
              <CalendarPlus size={14} className="mr-1.5 text-primary" />Extend rental
            </Button>
          )}
          {(canManage || b.can_edit) && <Button size="sm" variant="outline" onClick={openEdit} data-testid="action-edit"><PencilSimple size={14} className="mr-1.5" />Edit</Button>}
          {!isInternal && <Button size="sm" variant="outline" onClick={() => navigate(`/new-booking?duplicate=${id}`)} data-testid="action-duplicate"><CopySimple size={14} className="mr-1.5" />Duplicate</Button>}
          {!isInternal && <Button size="sm" variant="outline" onClick={() => setQuoteBooking(b)} data-testid="action-quote"><Printer size={14} className="mr-1.5" />Print / Quote</Button>}
          {!isInternal && canManage && (formType === "chauffeur" || formType === "tour") && (
            <Button size="sm" variant="outline" onClick={() => setInvoiceBooking(b)} data-testid="action-invoice"><Receipt size={14} className="mr-1.5 text-primary" />Generate Invoice</Button>
          )}
          {((canManage && active) || b.can_cancel) && (
            <Button size="sm" variant="outline" onClick={cancelBooking} data-testid="action-cancel"><XCircle size={14} className="mr-1.5" />Cancel</Button>
          )}
          {(canManage || b.can_delete) && <Button size="sm" variant="ghost" onClick={doDelete} data-testid="action-delete"><Trash size={14} className="mr-1.5 text-destructive" />Delete</Button>}
        </div>
      </div>

      <div className="border border-border bg-card p-6">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <div className="label-mono capitalize">{isInternal ? "Internal hold" : `${b.booking_type} booking`}</div>
            <h1 className="font-heading text-3xl mt-1" data-testid="booking-detail-name">{isInternal ? (b.internal_user || "Internal hold") : (b.customer_name || "Guest")}</h1>
            <div className="text-sm text-muted-foreground mt-1">{fmtDT(b.start_datetime)} → {fmtDT(b.end_datetime)}</div>
          </div>
          <span className="label-mono text-primary border border-primary/40 px-3 py-1.5" data-testid="booking-detail-status">
            {STATUS_LABEL[b.status] || b.status}
          </span>
        </div>
      </div>

      {isInternal && (
        <Section title="Internal hold">
          <Field label="Status" value={STATUS_LABEL[b.status] || b.status} />
          <Field label="Vehicle" value={b.vehicle_label} />
          <Field label="License Plate" value={b.vehicle_plate} />
          <Field label="Start" value={fmtDT(b.start_datetime)} />
          <Field label="Return" value={fmtDT(b.end_datetime)} />
          <Field label="Used by" value={b.internal_user} />
          <Field label="Reason" value={b.internal_reason} />
          <Field label="Booked by" value={b.created_by_name} />
        </Section>
      )}

      {!isInternal && (<>
      <Section title="Client Credentials">
        <Field label="Client Name" value={b.customer_name} />
        <Field label="Phone Number" value={b.customer_phone} />
        <Field label="Email" value={b.customer_email} />
        <Field label="Age" value={b.client_age} />
        <Field label="Credit Card" value={b.credit_card} />
        <Field label="Deposit Amount" value={b.deposit_amount} />
        <Field label="Insurance Coverage" value={yn(b.insurance_coverage)} />
        {b.insurance_coverage === "yes" ? <Field label="Insurance Called" value={yn(b.insurance_called)} /> : null}
        {b.insurance_coverage === "yes" ? <Field label="Insured" value={cap(b.insured_type)} /> : null}
        {b.insurance_coverage === "yes" ? <Field label="Insurance Type" value={policy(b.insurance_policy_type)} /> : null}
        {b.insurance_coverage === "yes" ? <Field label="Insurance Notes" value={b.insurance_notes} /> : null}
        {b.source === "website" && (
          <>
            <Field label="G / Class 6 licence" value={b.has_license_class6 === true ? "Yes" : b.has_license_class6 === false ? "No" : "—"} />
            <Field label="Has own insurance" value={b.has_insurance === true ? "Yes" : b.has_insurance === false ? "No" : "—"} />
            {b.has_insurance && <Field label="Rental coverage on policy" value={b.has_rental_coverage === true ? "Yes" : b.has_rental_coverage === false ? "No" : "—"} />}
            {b.has_insurance && <Field label="Insurance company" value={b.insurance_company} />}
            <Field label="CC with rental coverage" value={b.has_cc_rental_coverage === true ? "Yes" : b.has_cc_rental_coverage === false ? "No" : "—"} />
          </>
        )}
      </Section>

      <Section title="Booking Details">
        <Field label="Status" value={STATUS_LABEL[b.status] || b.status} />
        <Field label="Salesperson" value={b.salesperson} />
        {canEditPoints ? (
          <div data-testid="points-go-to">
            <div className="label-mono mb-1">Points go to</div>
            <Select value={b.points_rep || "__default__"} onValueChange={setPointsRep}>
              <SelectTrigger className="rounded-xl" data-testid="points-rep-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__default__" data-testid="points-rep-default">
                  Default — {creditedRep(b.salesperson) || "no salesperson"}
                </SelectItem>
                {salespeople.map((s) => (
                  <SelectItem key={s.id} value={s.name} data-testid={`points-rep-option-${s.name}`}>{s.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="text-xs text-muted-foreground mt-1">Changes points only — not the salesperson.</div>
          </div>
        ) : (
          <Field label="Points go to" value={(b.points_rep || "").trim() || creditedRep(b.salesperson) || undefined} />
        )}
        <Field label="Booking Date" value={b.booking_date} />
        <Field label="Category" value={cap(b.booking_category)} />
        <Field label="Service Type" value={cap(b.service_type_detail)} />
        <Field label="Vehicle" value={b.vehicle_label} />
        <Field label="License Plate" value={b.vehicle_plate} />
        <Field label="Driver" value={b.driver_name} />
        <Field label="Start" value={fmtDT(b.start_datetime)} />
        <Field label="Return" value={fmtDT(b.end_datetime)} />
        <Field label="Start Location" value={b.start_location} />
        <Field label="End Location" value={b.end_location} />
        <Field label="In-between Stops" value={b.in_between_stops} />
        <Field label="Duration" value={b.duration} />
      </Section>

      <Section title="Financials">
        <Field label="Payment" value={b.payment_status === "paid" ? "Paid ✓" : undefined} />
        <Field label="Rate" value={money(b.rental_rate)} />
        <Field label="Delivery" value={money(b.delivery)} />
        <Field label="Total" value={b.tax_applied ? `${money(bookingTotal(b))} (incl. HST)` : money(b.total || b.amount)} />
        <Field label="Pre-tax (no delivery)" value={money(b.pre_tax_no_delivery)} />
        <Field label="Gas Filled" value={b.gas_filled} />
        <Field label="Start KM" value={b.start_km} />
        <Field label="End KM" value={b.end_km} />
        <Field label="Deposit Sent" value={money(b.depo_sent)} />
        <Field label="Remaining Balance" value={money(b.remaining_balance)} />
        {user?.role === "admin" && (
          <>
            <Field label="Driver Cost" value={money(b.driver_cost)} />
            <Field label="Gas Cost" value={money(b.gas_cost)} />
            <Field label="Other Costs" value={money(b.other_costs)} />
            <Field label="Cash Owed" value={money(b.cash_owed)} />
            <Field label="Their Cut" value={money(b.their_cut)} />
            <Field label="Our Cut" value={money(b.our_cut)} />
          </>
        )}
      </Section>

      {user?.role === "admin" && formType === "chauffeur" && (
        <ChauffeurSplitSection key={b.id} ref={splitRef} booking={b} onSaved={loadBooking} />
      )}

      <Section title="Tax">
        <div className="col-span-full flex items-center justify-between">
          <div>
            <div className="label-mono">Add Tax (HST {Math.round(TAX_RATE * 100)}%)</div>
            <div className="text-xs text-muted-foreground">{b.tax_applied ? "Tax is included in the total below." : "Toggle on to add tax to the total."}</div>
          </div>
          <button type="button" onClick={toggleTax} data-testid="detail-tax-toggle"
            className={`relative inline-flex h-7 w-14 items-center rounded-full transition-colors ${b.tax_applied ? "bg-primary" : "bg-secondary border border-border"}`}>
            <span className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${b.tax_applied ? "translate-x-8" : "translate-x-1"}`} />
          </button>
        </div>
        {b.tax_applied && (
          <div className="col-span-full mt-2 border border-primary/40 bg-primary/5 p-4" data-testid="detail-tax-box">
            <div className="flex justify-between text-sm"><span className="text-muted-foreground">Subtotal</span><span>{money(b.total || b.amount)}</span></div>
            <div className="flex justify-between text-sm mt-1"><span className="text-muted-foreground">Tax (HST {Math.round(TAX_RATE * 100)}%)</span><span>{money((Number(b.total || b.amount) || 0) * TAX_RATE)}</span></div>
            <div className="flex justify-between font-heading text-lg mt-2 pt-2 border-t border-border"><span>Total + Tax</span><span data-testid="detail-total-with-tax">{money((Number(b.total || b.amount) || 0) * (1 + TAX_RATE))}</span></div>
          </div>
        )}
      </Section>

      <Section title="Notes">
        <div className="col-span-full text-sm whitespace-pre-wrap">{b.notes || "—"}</div>
      </Section>
      </>)}

      {/* SALES-014: extension history, oldest first. Absent entirely — no empty
          container — on a booking that has never been extended. */}
      {extensions.length > 0 && (
        <div className="border border-border bg-card p-6" data-testid="booking-extensions">
          <div className="label-mono text-primary mb-4">Extension history</div>
          <ul className="space-y-3">
            {extensions.map((x, i) => (
              <li key={x.id || i} className="border border-border/60 bg-secondary/20 p-4" data-testid={`booking-extension-${i}`}>
                {/* Left-aligned rather than `justify-between`: uncapped, the
                    timestamp sat 1,105px away from the days at 1920 and the
                    entry read as two unrelated fragments. */}
                <div className="flex items-baseline gap-x-3 gap-y-1 flex-wrap">
                  <span className="text-sm font-medium text-primary" data-testid={`booking-extension-${i}-days`}>
                    {/* SALES-027: display through the helper from the record's two
                        stored dates — never its stored days_added — so old
                        fractional records show whole calendar days without being
                        rewritten. */}
                    + {dayLabel(extensionDays(x.previous_end_datetime, x.new_end_datetime) ?? x.days_added)}
                    <span className="text-foreground font-normal">
                      {" · "}{Number(x.amount_added) > 0 ? `${money(x.amount_added)} added` : "no charge"}
                    </span>
                  </span>
                  <span className="text-xs text-muted-foreground">
                    {fmtDT(x.extended_at)}{x.extended_by_name ? ` · ${x.extended_by_name}` : ""}
                  </span>
                </div>
                <div className="text-sm text-muted-foreground mt-1.5" data-testid={`booking-extension-${i}-window`}>
                  {fmtDT(x.previous_end_datetime)} → {fmtDT(x.new_end_datetime)}
                  {x.rate || x.rate === 0 ? ` · ${money(x.rate)}/day` : ""}
                </div>
                {x.notes ? (
                  <div className="text-sm mt-1.5 whitespace-pre-wrap" data-testid={`booking-extension-${i}-notes`}>{x.notes}</div>
                ) : null}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Extend rental dialog */}
      <Dialog open={extendOpen} onOpenChange={setExtendOpen}>
        <DialogContent className="bg-card border-border max-w-md" data-testid="detail-extend-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">Extend rental</DialogTitle>
            <DialogDescription>
              Moves the return date, adds what you are charging for the extra days, and records all of it on this booking — who did it, when, how many days and how much.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <DateTimeField
              label="New end of rental"
              value={extendEnd}
              onChange={setExtendEnd}
              min={b.end_datetime}
              required
              testid="detail-extend-end"
            />
            <div className="border border-border bg-secondary/30 p-3 space-y-1" data-testid="detail-extend-summary">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Current end</span>
                <span data-testid="detail-extend-current-end">{fmtDT(b.end_datetime) || "—"}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Days added</span>
                <span data-testid="detail-extend-days">
                  {extendDays === null ? "—" : extendIsLater ? dayLabel(extendDays) : "Must be after the current end"}
                </span>
              </div>
              {extendIsLater && extendDays === 0 && (
                <p className="text-xs text-amber-500 pt-1" data-testid="detail-extend-sameday-hint">
                  Same day — no calendar day is added. Enter an amount if you're charging for the extra hours.
                </p>
              )}
            </div>
            <div>
              <Label className="label-mono mb-1.5 block">Extension rate per day (optional)</Label>
              <MoneyInput value={extendRate} onChange={setExtendRate} testid="detail-extend-rate" />
              <p className="text-xs text-muted-foreground mt-1.5" data-testid="detail-extend-rate-hint">
                {`Booking rate is ${money(b.rental_rate) || "—"}/day. An extension is often re-rated — this is stored as the record of why the amount was what it was.`}
              </p>
            </div>
            <div>
              <Label className="label-mono mb-1.5 block">Amount to add <span className="text-primary">*</span></Label>
              <MoneyInput value={extendAmount} onChange={(v) => { setAmountTouched(true); setExtendAmount(v); }} testid="detail-extend-amount" />
              <p className="text-xs text-muted-foreground mt-1.5" data-testid="detail-extend-amount-hint">
                {extendRate !== "" && !amountTouched
                  ? `Pre-tax, from ${money(extendRate)} × ${extendDays > 0 ? dayLabel(extendDays) : "days"} — edit it for a flat or discounted price.`
                  : "Pre-tax, like the booking total. Enter 0 to record an extension you are not charging for."}
              </p>
            </div>
            <div className="border border-primary/40 bg-primary/5 p-3 space-y-1" data-testid="detail-extend-total-box">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Current total</span>
                <span data-testid="detail-extend-current-total">{money(baseTotal)}</span>
              </div>
              <div className="flex justify-between font-heading text-lg pt-1 border-t border-border">
                <span>New total</span>
                <span data-testid="detail-extend-new-total">{money(newTotal)}</span>
              </div>
              {b.tax_applied && (
                <div className="flex justify-between text-xs text-muted-foreground">
                  <span>Client pays (incl. HST {Math.round(TAX_RATE * 100)}%)</span>
                  <span data-testid="detail-extend-new-total-with-tax">
                    {money(Math.round(newTotal * (1 + TAX_RATE) * 100) / 100)}
                  </span>
                </div>
              )}
              {extendDays > 0 && addAmount === 0 && (
                <p className="text-xs text-amber-500 pt-1" data-testid="detail-extend-free-note">
                  Free extension — the total does not change and nothing is billed for these days.
                </p>
              )}
            </div>
            <div>
              <Label className="label-mono mb-1.5 block">Note (optional)</Label>
              <Textarea rows={3} value={extendNotes} onChange={(e) => setExtendNotes(e.target.value)}
                placeholder="e.g. Client kept the car for the long weekend" className="bg-input border-border"
                data-testid="detail-extend-notes" />
            </div>
          </div>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setExtendOpen(false)} data-testid="detail-extend-cancel">Cancel</Button>
            <Button onClick={doExtend} disabled={extending || !extendIsLater}
              className="bg-primary text-primary-foreground hover:bg-primary/90" data-testid="detail-extend-confirm">
              <CalendarPlus size={14} className="mr-1.5" />{extending ? "Recording…" : "Record extension"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto bg-card border-border" data-testid="detail-edit-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">Edit {formType === "rental" ? "Rental" : formType === "tour" ? "Tour" : formType === "internal" ? "Internal Hold" : "Chauffeur"} Booking</DialogTitle>
            <DialogDescription>Update the booking details below, then save.</DialogDescription>
          </DialogHeader>
          {isInternal ? (
            <div className="space-y-4" data-testid="internal-edit-form">
              <div>
                <Label className="label-mono mb-1.5 block">Vehicle</Label>
                <Select value={form.vehicle_id || ""} onValueChange={handleVehicleChange}>
                  <SelectTrigger data-testid="internal-edit-vehicle"><SelectValue placeholder="Select a vehicle" /></SelectTrigger>
                  <SelectContent>
                    {vehicles.map((v) => (
                      <SelectItem key={v.id} value={v.id}>{v.make} {v.model}{v.year ? ` ${v.year}` : ""}{v.license_plate ? ` · ${v.license_plate}` : ""}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div data-testid="internal-edit-start">
                  <DateTimeField label="Start" value={form.start_datetime} onChange={(v) => setForm({ ...form, start_datetime: v })} required testid="internal-edit-start" />
                </div>
                <div data-testid="internal-edit-end">
                  <DateTimeField label="End" value={form.end_datetime} onChange={(v) => setForm({ ...form, end_datetime: v })} min={form.start_datetime} required testid="internal-edit-end" />
                </div>
              </div>
              <div>
                <Label className="label-mono mb-1.5 block">Used by</Label>
                <Input value={form.internal_user || ""} onChange={(e) => setForm({ ...form, internal_user: e.target.value })} placeholder="e.g. Content team — Alex" data-testid="internal-edit-user" />
              </div>
              <div>
                <Label className="label-mono mb-1.5 block">Reason</Label>
                <Textarea rows={3} value={form.internal_reason || ""} onChange={(e) => setForm({ ...form, internal_reason: e.target.value })} placeholder="e.g. Photo shoot" className="bg-input border-border" data-testid="internal-edit-reason" />
              </div>
            </div>
          ) : (
          <BookingForm
            formType={formType}
            form={form}
            setForm={setForm}
            vehicles={vehicles}
            chauffeurs={chauffeurs}
            onVehicleChange={handleVehicleChange}
            onDriverChange={handleDriverChange}
            excludeBookingId={id}
            salespersonReadOnly={!canManage}
          />
          )}
          <DialogFooter className="mt-6">
            <Button variant="outline" onClick={() => setEditOpen(false)}>Cancel</Button>
            <Button onClick={saveEdit} disabled={saving} className="bg-primary text-primary-foreground hover:bg-primary/90" data-testid="detail-edit-save">
              {saving ? "Saving…" : "Save changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Send back (reject) dialog */}
      <Dialog open={rejectOpen} onOpenChange={setRejectOpen}>
        <DialogContent className="bg-card border-border max-w-md" data-testid="detail-sendback-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">Send back to agent</DialogTitle>
            <DialogDescription>This returns the booking to the agent's Rejected Bookings so they can edit and resubmit it.</DialogDescription>
          </DialogHeader>
          <div className="mt-2">
            <Label className="label-mono mb-1.5 block">Reason / what to fix (optional)</Label>
            <Textarea rows={4} value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} placeholder="e.g. Missing deposit amount, wrong dates…" className="bg-input border-border" data-testid="detail-sendback-reason" />
          </div>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setRejectOpen(false)}>Cancel</Button>
            <Button onClick={sendBack} className="bg-amber-600 text-white hover:bg-amber-600/90" data-testid="detail-sendback-confirm"><ArrowUUpLeft size={14} className="mr-1.5" />Send back</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <QuoteDialog booking={quoteBooking} onClose={() => setQuoteBooking(null)} />
      <InvoiceDialog booking={invoiceBooking} onClose={() => setInvoiceBooking(null)} onSaved={() => { loadBooking(); }} />
    </div>
  );
}
